/**
 * App - Main application component with dynamic routing
 * 
 * SOLID Principles:
 * - Single Responsibility: Manages routing and authentication flow
 * - Open/Closed: Add modules to registry, routes generated automatically
 * - Dependency Inversion: Routes generated from configuration (moduleRegistry)
 */

import { useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthBootstrap } from './hooks/shared/auth'
import { getRuntimeEnv } from './lib/runtime-env'

const LANDING_URL = getRuntimeEnv('VITE_LANDING_URL') || 'https://ofertator.pl/'

function ExternalRedirect({ to }: { to: string }) {
  useEffect(() => {
    window.location.replace(to)
  }, [to])
  return null
}

/** Keep query string (e.g. ?ref=) when aliasing short auth paths. */
function AuthAliasRedirect({ to }: { to: string }) {
  const { search } = useLocation()
  return <Navigate to={`${to}${search}`} replace />
}
import ProtectedRoute from './components/auth/ProtectedRoute'
import MainLayout from './components/layout/MainLayout'
import ToastContainer from './components/ui/ToastContainer'
import { useAuthStore } from './store/authStore'
import { FeatureFlagProvider, useFeatureFlags } from './contexts/FeatureFlagContext'
import { generateModuleRoutes, generateSpecialRoutes } from './config/routeGenerator'
import DocumentAcceptanceModal from './components/auth/DocumentAcceptanceModal'
import MarketingConsentModal from './components/marketing/MarketingConsentModal'
import FeedbackWidget from './components/feedback/FeedbackWidget'
import FeatureSurveyPopup from './components/feature-survey/FeatureSurveyPopup'
import OlimpConferencePromo from './components/promotions/OlimpConferencePromo'
import CookieConsentBanner from './components/cookies/CookieConsent'
import SessionResumeWatcher from './components/auth/SessionResumeWatcher'
import LanguageProvider from './i18n/LanguageProvider'
import AuthLayout from './components/layout/AuthLayout'
import PublicPageLayout from './components/layout/PublicPageLayout'

// Auth pages (not in registry - always public)
import Login from './pages/auth/Login'
import Register from './pages/auth/Register'
import VerifyEmail from './pages/auth/VerifyEmail'
import ResendVerification from './pages/auth/ResendVerification'
import ForgotPassword from './pages/auth/ForgotPassword'
import ResetPassword from './pages/auth/ResetPassword'
import RegisterAfterPayment from './pages/auth/RegisterAfterPayment'
import NotificationUnsubscribe from './pages/notifications/NotificationUnsubscribe'
import OAuthAuthorize from './pages/oauth/Authorize'
import PartnerSignupPage from './pages/partners/PartnerSignupPage'
import PartnerDashboardPage from './pages/partners/PartnerDashboardPage'

// Payment pages (marketing landing is now hosted on Framer at app.ofertator.pl)
import PaymentSuccess from './pages/landing/PaymentSuccess'


function AppRoutes() {
  const { isMarketplaceEnabled, isAIConfigEnabled, isUsersAnalyticsEnabled, isAIUsageEnabled, isUserAIConfigEnabled, isLoading } = useFeatureFlags()
  const { isAuthenticated } = useAuthStore()
  const { t } = useTranslation('common')
  
  // Show loading state while feature flags are loading
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('loadingConfig')}</p>
        </div>
      </div>
    )
  }
  
  return (
    <Routes>
        {/* Root: authenticated → dashboard, anonymous → Framer landing on app.ofertator.pl.
            Marketing landing, shop, and module info pages all live on Framer now. */}
        <Route
          path="/"
          element={
            isAuthenticated
              ? <Navigate to="/dashboard" replace />
              : <ExternalRedirect to={LANDING_URL} />
          }
        />
        <Route path="/landing" element={<ExternalRedirect to={LANDING_URL} />} />
        <Route path="/shop" element={<ExternalRedirect to={`${LANDING_URL}#cennik`} />} />
        <Route path="/info/*" element={<ExternalRedirect to={LANDING_URL} />} />

        {/* Public auth routes */}
        <Route path="/auth/login" element={<AuthLayout><Login /></AuthLayout>} />
        <Route path="/auth/register" element={<AuthLayout><Register /></AuthLayout>} />
        {/* Short aliases — preserve ?ref= for invite links */}
        <Route path="/login" element={<AuthAliasRedirect to="/auth/login" />} />
        <Route path="/register" element={<AuthAliasRedirect to="/auth/register" />} />
        <Route path="/auth/verify/:token" element={<AuthLayout><VerifyEmail /></AuthLayout>} />
        <Route path="/auth/resend-verification" element={<AuthLayout><ResendVerification /></AuthLayout>} />
        <Route path="/auth/forgot-password" element={<AuthLayout><ForgotPassword /></AuthLayout>} />
        <Route path="/auth/reset-password/:token" element={<AuthLayout><ResetPassword /></AuthLayout>} />
        <Route path="/auth/register-after-payment" element={<AuthLayout><RegisterAfterPayment /></AuthLayout>} />
        <Route
          path="/notifications/unsubscribe"
          element={
            <AuthLayout>
              <NotificationUnsubscribe />
            </AuthLayout>
          }
        />
        <Route path="/oauth/authorize" element={<OAuthAuthorize />} />

        {/* Stripe payment routes */}
        <Route path="/payment-success" element={<PublicPageLayout><PaymentSuccess /></PublicPageLayout>} />

        {/* Partner referral program (no Ofertator account) */}
        <Route path="/partners" element={<PublicPageLayout><PartnerSignupPage /></PublicPageLayout>} />
        <Route path="/partners/dashboard" element={<PublicPageLayout><PartnerDashboardPage /></PublicPageLayout>} />
        
        {/* Protected application routes */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Routes>
                  {/* 
                    Dynamic routes generated from moduleRegistry 
                    - Includes main routes and legacy backward-compatible routes
                    - Automatically protected based on module configuration
                    - Filtered by feature flags (hides disabled marketplaces)
                  */}
                  {generateModuleRoutes(isMarketplaceEnabled)}
                  {generateSpecialRoutes({
                    isAIConfigEnabled,
                    isUsersAnalyticsEnabled,
                    isAIUsageEnabled,
                    isUserAIConfigEnabled
                  })}

                  {/* Catch all - redirect to dashboard */}
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </MainLayout>
            </ProtectedRoute>
          }
        />
      </Routes>
  )
}

export default function App() {
  const isSessionReady = useAuthBootstrap()
  const initializePermissions = useAuthStore((state) => state.initializePermissions)
  const showDocumentModal = useAuthStore((state) => state.showDocumentModal)
  const showMarketingModal = useAuthStore((state) => state.showMarketingModal)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const user = useAuthStore((state) => state.user)
  const documentAccepted = useAuthStore((state) => state.documentAccepted)
  const getCurrentUser = useAuthStore((state) => state.getCurrentUser)
  const { t } = useTranslation('common')

  // Initialize permissions on app start if user is already logged in
  useEffect(() => {
    if (isSessionReady) {
      initializePermissions()
    }
  }, [initializePermissions, isSessionReady])

  const handleDocumentAccepted = async () => {
    // Refresh user data to get updated document acceptance status
    await getCurrentUser()
    documentAccepted()
  }

  return (
    <LanguageProvider>
      {!isSessionReady ? (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('loadingConfig')}</p>
          </div>
        </div>
      ) : (
        <FeatureFlagProvider>
          <FeedbackWidget>
            <SessionResumeWatcher enabled={isAuthenticated} />
            <AppRoutes />

            {/* Document acceptance modal - blocking, shown when legal docs are outdated */}
            {showDocumentModal && user?.missing_documents && (
              <DocumentAcceptanceModal
                isOpen={showDocumentModal}
                missingDocuments={user.missing_documents}
                onAccepted={handleDocumentAccepted}
              />
            )}

            {/* Marketing consent modal - non-blocking, shown once after legal docs are accepted */}
            {!showDocumentModal && (
              <MarketingConsentModal isOpen={showMarketingModal} />
            )}

            {/* Time-boxed event promo — below legal/marketing priority */}
            <OlimpConferencePromo
              isAuthenticated={isAuthenticated}
              blockedByHigherPriority={showDocumentModal || showMarketingModal}
            />

            {/* Cookie consent banner - shown globally on all pages */}
            <CookieConsentBanner />

            <FeatureSurveyPopup isAuthenticated={isAuthenticated} />

            {/* Toast notifications - shown globally */}
            <ToastContainer />
          </FeedbackWidget>
        </FeatureFlagProvider>
      )}
    </LanguageProvider>
  )
}
